import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { AirplaneTilt, Lock, At, Warning } from "@phosphor-icons/react";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";

const BG = "https://images.unsplash.com/photo-1545132147-d037e6c54cfd?crop=entropy&cs=srgb&fm=jpg&w=1920&q=70";

export default function Login() {
  const { user, login } = useAuth();
  const [email, setEmail] = useState("admin@saafares.com");
  const [password, setPassword] = useState("Admin@123");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      await login(email, password);
      toast.success("Welcome to SAA Fare Tracker");
      nav("/", { replace: true });
    } catch (e) {
      setErr(e.response?.data?.detail || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex relative" style={{ background: "var(--navy)" }}>
      {/* Left: bg image */}
      <div className="hidden md:flex md:flex-1 relative">
        <img src={BG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-50" />
        <div className="absolute inset-0" style={{
          background: "linear-gradient(135deg, rgba(10,20,40,0.6), rgba(10,20,40,0.92))",
        }} />
        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          <div className="flex items-center gap-3">
            <div
              className="w-12 h-12 rounded-full flex items-center justify-center"
              style={{ background: "var(--saa-gold)" }}
            >
              <AirplaneTilt size={24} weight="duotone" color="#0A1428" className="airplane-anim" />
            </div>
            <div>
              <div className="font-display text-xl font-bold leading-none">South African Airways</div>
              <div className="text-xs mt-1 uppercase tracking-[0.18em]" style={{ color: "var(--saa-gold)" }}>
                Commercial Fare Tracker
              </div>
            </div>
          </div>

          <div>
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="font-display text-4xl lg:text-5xl font-bold leading-tight tracking-tight"
            >
              Stay ahead of every <br />
              <span style={{ color: "var(--saa-gold)" }}>competitor fare</span>.
            </motion.h1>
            <p className="mt-4 text-sm max-w-md" style={{ color: "var(--text-secondary)" }}>
              Live monitoring across 24 strategic routes from Australasia to Southern Africa.
              Cascading multi-source pipeline with SerpAPI, Zyte, and airline-direct feeds.
            </p>
          </div>
        </div>
      </div>

      {/* Right: form */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-12">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="w-full max-w-md saa-card p-8"
        >
          <div className="md:hidden flex items-center gap-3 mb-6">
            <div
              className="w-11 h-11 rounded-full flex items-center justify-center"
              style={{ background: "var(--saa-gold)" }}
            >
              <AirplaneTilt size={22} weight="duotone" color="#0A1428" />
            </div>
            <div className="font-display text-lg font-bold">SAA Fare Tracker</div>
          </div>
          <h2 className="font-display text-2xl font-bold">Welcome back</h2>
          <p className="text-sm mt-1" style={{ color: "var(--text-secondary)" }}>
            Sign in to view live competitor fare data.
          </p>

          <form onSubmit={submit} className="mt-6 space-y-4">
            <div>
              <label className="text-xs uppercase tracking-[0.15em]" style={{ color: "var(--text-secondary)" }}>
                Email
              </label>
              <div className="relative mt-1">
                <At
                  size={16}
                  className="absolute left-3 top-1/2 -translate-y-1/2"
                  style={{ color: "var(--text-muted)" }}
                />
                <Input
                  data-testid="login-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="pl-9 h-11"
                />
              </div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.15em]" style={{ color: "var(--text-secondary)" }}>
                Password
              </label>
              <div className="relative mt-1">
                <Lock
                  size={16}
                  className="absolute left-3 top-1/2 -translate-y-1/2"
                  style={{ color: "var(--text-muted)" }}
                />
                <Input
                  data-testid="login-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="pl-9 h-11"
                />
              </div>
            </div>

            {err && (
              <div
                data-testid="login-error"
                className="flex items-center gap-2 text-sm px-3 py-2 rounded-md"
                style={{
                  background: "rgba(239,68,68,0.10)",
                  border: "1px solid rgba(239,68,68,0.30)",
                  color: "var(--danger)",
                }}
              >
                <Warning size={14} weight="fill" />
                {err}
              </div>
            )}

            <Button
              data-testid="login-submit"
              type="submit"
              disabled={busy}
              className="w-full h-11 font-semibold"
              style={{ background: "var(--saa-gold)", color: "#0A1428" }}
            >
              {busy ? "Signing in..." : "Sign in"}
            </Button>
          </form>
        </motion.div>
      </div>
    </div>
  );
}
